import React from 'react';
import LegalEmail from './legal/LegalEmail';
import { LEGAL_LAST_UPDATED } from '../config/legal';
import { WEBSITE_URL, cameFromWebsite } from './legal/siteReturn';

// Privacy Policy (Hebrew). The same text is embedded in ConsentScreen.jsx (LEGAL_DOCS.privacy) — the version the
// user accepts at registration; legal.texts.test.jsx keeps the two identical. Revision history: LEGAL_REVISIONS.md.
const PrivacyPolicy = () => {
  const fromSite = cameFromWebsite();
  return (
    <div className="min-h-screen bg-gray-900 text-white p-6 md:p-12" dir="rtl">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 text-center">מדיניות פרטיות - RitmoIQ</h1>
        <p className="text-gray-400 text-center mb-8">עודכן לאחרונה: {LEGAL_LAST_UPDATED.he}</p>

        {/* מבוא */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-blue-400">1. מבוא</h2>
          <p className="text-gray-300 leading-relaxed">
            RitmoIQ ("האפליקציה", "אנחנו", "שלנו") מחויבת להגן על פרטיות המשתמשים שלנו. RitmoIQ הוא פרויקט המופעל באופן פרטי על ידי ליאור פרובקביץ, ואינו חברה מאוגדת.
            מדיניות פרטיות זו מסבירה כיצד אנו אוספים, משתמשים ומגנים על המידע שלך בעת השימוש באפליקציה שלנו.
          </p>
        </section>

        {/* מידע שאנו אוספים */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-blue-400">2. מידע שאנו אוספים</h2>

          <h3 className="text-lg font-medium mb-2 text-gray-200">2.1 מידע שאתה מספק לנו:</h3>
          <ul className="list-disc list-inside text-gray-300 mb-4 space-y-1">
            <li>שם ופרטי קשר (אימייל)</li>
            <li>פרטי פרופיל ריצה (תאריך לידה, רמה, שיאים אישיים). תאריך הלידה נשמר כדי לחשב את הגיל שלך באופן אוטומטי; משתמשים ותיקים שלא הזינו תאריך לידה — הגיל שהוזן נשמר כפי שהוא</li>
            <li>מידע גופני ובריאותי הרלוונטי לאימון, אם תבחר/י למסור אותו (כגון משקל, דופק מרבי והיסטוריית פציעות)</li>
            <li>יעדי אימון ומרוצים</li>
            <li>שיחות עם מאמן ה-AI</li>
          </ul>

          <h3 className="text-lg font-medium mb-2 text-gray-200">2.2 מידע שהתקבל מ-Strava (החיבור הופסק):</h3>
          <p className="text-gray-300 mb-2">משתמשים שחיברו בעבר את Strava נתנו הסכמה לקבלת המידע הבא מ-Strava:</p>
          <ul className="list-disc list-inside text-gray-300 mb-2 space-y-1">
            <li>נתוני פעילויות ריצה (תאריך, מרחק, זמן, קצב, דופק וספליטים)</li>
            <li>שם פרופיל ותמונה מ-Strava</li>
            <li>היסטוריית אימונים</li>
          </ul>
          <p className="text-gray-300 mb-4">החל מ-21 באוקטובר 2026 איננו מקבלים מידע חדש מ-Strava (ראו סעיף 8).</p>

          <h3 className="text-lg font-medium mb-2 text-gray-200">2.3 מידע טכני:</h3>
          <ul className="list-disc list-inside text-gray-300 space-y-1">
            <li>כתובת IP</li>
            <li>סוג דפדפן ומכשיר</li>
            <li>זמני גישה לאפליקציה</li>
          </ul>

          <h3 className="text-lg font-medium mb-2 mt-4 text-gray-200">2.4 התחברות עם Google:</h3>
          <p className="text-gray-300 mb-2">אם בחרת להתחבר עם Google, Google מעבירה לשירות ההתחברות שלנו (Firebase Authentication, שירות של Google) את כתובת האימייל, השם ותמונת הפרופיל של חשבון Google שלך. RitmoIQ משתמשת בכתובת האימייל ובמזהה החשבון כדי לזהות אותך ולאמת את הגישה לפיילוט. השם ותמונת הפרופיל מחשבון Google אינם נשמרים במסד הנתונים של RitmoIQ ואינם נשלחים לספקי ה-AI. איננו מקבלים את הסיסמה של חשבון Google שלך.</p>
          <p className="text-gray-300">ההתחברות עם Google היא לבחירתך: אפשר גם להתחבר עם אימייל וסיסמה, ולהגדיר סיסמה מתוך הפרופיל. ניתן להסיר את הגישה של RitmoIQ בהגדרות האבטחה של חשבון Google שלך; הסרה זו אינה מוחקת את החשבון שלך ב-RitmoIQ (ראו סעיף 7).</p>
        </section>

        {/* כיצד אנו משתמשים במידע */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-blue-400">3. כיצד אנו משתמשים במידע</h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li><strong>אימון מותאם אישית:</strong> ניתוח האימונים שלך ויצירת תכניות אימון מותאמות</li>
            <li><strong>משוב ותובנות AI:</strong> הפקת ניתוח אימונים, משוב ותובנות מותאמים אישית באמצעות בינה מלאכותית (ראו סעיף 4.1)</li>
            <li><strong>מעקב התקדמות:</strong> הצגת נתונים וסטטיסטיקות על הריצות שלך</li>
            <li><strong>תקשורת:</strong> שליחת התראות ועדכונים על האימונים שלך</li>
            <li><strong>שיפור השירות:</strong> ניתוח שימוש כללי לשיפור האפליקציה</li>
          </ul>
        </section>

        {/* שיתוף מידע */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-blue-400">4. שיתוף מידע עם צדדים שלישיים</h2>
          <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-4 mb-4">
            <p className="text-green-300 font-medium">
              ✅ אנחנו לא מוכרים, משכירים או משתפים את המידע האישי שלך עם צדדים שלישיים למטרות שיווק.
            </p>
          </div>
          <p className="text-gray-300 mb-2">אנו משתפים מידע רק במקרים הבאים:</p>
          <ul className="list-disc list-inside text-gray-300 space-y-1">
            <li><strong>ספקי שירות:</strong> שרתים ותשתיות (Render, Vercel) לצורך הפעלת האפליקציה, ו-Google Firebase להתחברות (Firebase Authentication) ולהתראות (Firebase Cloud Messaging)</li>
            <li><strong>AI:</strong> OpenAI, באמצעות שירות OpenAI API, ו-Google, באמצעות שירות Gemini API, כספקי עיבוד בינה מלאכותית (ראו סעיף 4.1)</li>
            <li><strong>Garmin (פיילוט סגור):</strong> Stridee, כספק החיבור ל-Garmin Connect (ראו סעיף 9)</li>
            <li><strong>דרישה חוקית:</strong> אם נדרש על פי חוק</li>
          </ul>
          <h3 className="text-lg font-medium mb-2 mt-4 text-gray-200">4.1 עיבוד באמצעות בינה מלאכותית (OpenAI API ו-Google Gemini API)</h3>
          <p className="text-gray-300 mb-2">RitmoIQ משתמשת בשני ספקי עיבוד בינה מלאכותית, בשירותי ה-API בתשלום שלהם, כדי להפיק עבורך אימון מותאם אישית, ניתוח אימונים ותובנות: OpenAI (OpenAI API) — המודל הראשי של מאמן ה-AI בשיחות; ו-Google (Gemini API) — בשיחות שבהן מדווח כאב, פציעה או חשד לפציעה, לניתוח עומק של אימון, לתובנות ולהתאמת תוכנית האימונים, וכגיבוי כאשר השירות של OpenAI אינו זמין.</p>
          <p className="text-gray-300 mb-2">כאשר הדבר נדרש לצורך האימון שלך, המידע הבא עשוי להישלח לספקים אלה:</p>
          <ul className="list-disc list-inside text-gray-300 space-y-1">
            <li>פרטי פרופיל ריצה (כגון רמה, וכן גיל — מחושב מתאריך הלידה — ומשקל אם נמסרו). תאריך הלידה המלא אינו נשלח לספקי ה-AI, רק הגיל המחושב</li>
            <li>יעדי אימון ומרוצים</li>
            <li>היסטוריית פציעות רלוונטית</li>
            <li>דופק מרבי</li>
            <li>תוכנית האימונים שלך</li>
            <li>נתוני אימונים ופעילויות מ-Garmin, למשתמשים שאישרו את סעיף ה-AI בהסכמת Garmin (ראו סעיף 9), כגון תאריך, מרחק, משך, קצב, דופק וספליטים</li>
            <li>הודעות צ'אט רלוונטיות</li>
            <li>שמך כפי שהוזן בפרופיל (בדרך כלל שם פרטי)</li>
          </ul>
          <p className="text-gray-300 mt-2">בהתאם למימוש הנוכחי, כתובת האימייל, נתוני GPS/מסלול, תמונות ומידע שהתקבל מ-Strava (לרבות סיכומים וניתוחים שהופקו ממנו) אינם נשלחים לספקי ה-AI.</p>
          <p className="text-gray-300 mb-2">RitmoIQ אינה משתמשת במידע שלך כדי לאמן מודלי AI משלה. עיבוד המידע על ידי OpenAI כפוף ל<a href="https://openai.com/policies/services-agreement/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">תנאי השימוש של OpenAI API</a>, ועיבוד המידע על ידי Google כפוף ל<a href="https://ai.google.dev/gemini-api/terms" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">תנאי השימוש של Gemini API</a>.</p>
        </section>

        {/* אבטחת מידע */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-blue-400">5. אבטחת מידע</h2>
          <p className="text-gray-300 mb-2">אנו נוקטים באמצעים לאבטחת המידע שלך:</p>
          <ul className="list-disc list-inside text-gray-300 space-y-1">
            <li>הצפנת נתונים בתעבורה (HTTPS)</li>
            <li>אחסון מאובטח בשרתים מוגנים</li>
            <li>גישה מוגבלת למידע רגיש</li>
            <li>אימות משתמשים באמצעות Firebase Authentication (אימייל וסיסמה, או Google)</li>
          </ul>
        </section>

        {/* זכויות המשתמש */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-blue-400">6. הזכויות שלך (GDPR)</h2>
          <p className="text-gray-300 mb-2">יש לך את הזכויות הבאות לגבי המידע שלך:</p>
          <ul className="list-disc list-inside text-gray-300 space-y-1">
            <li><strong>זכות גישה:</strong> לבקש עותק של המידע שאנו מחזיקים עליך</li>
            <li><strong>זכות תיקון:</strong> לתקן מידע שגוי</li>
            <li><strong>זכות מחיקה:</strong> לבקש מחיקת כל המידע שלך</li>
            <li><strong>זכות ניוד:</strong> לקבל את המידע שלך בפורמט קריא</li>
            <li><strong>זכות התנגדות:</strong> להתנגד לעיבוד מסוים של המידע</li>
          </ul>
          <p className="text-gray-300 mt-4">
            למימוש זכויות אלה, פנה אלינו בכתובת: <LegalEmail />
          </p>
        </section>

        {/* מחיקת חשבון */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-blue-400">7. מחיקת חשבון ונתונים</h2>
          <p className="text-gray-300 mb-2">תוכל/י למחוק את החשבון והנתונים שלך בכל עת:</p>
          <ul className="list-disc list-inside text-gray-300 space-y-1">
            <li>מתוך האפליקציה: פרופיל ← מחיקת חשבון</li>
            <li>בפנייה אלינו בכתובת: <LegalEmail /></li>
          </ul>
          <p className="text-gray-300 mt-2">מחיקת החשבון מתוך הפרופיל מוחקת את נתוני החשבון שלך ממסד הנתונים של RitmoIQ, לרבות הפרופיל, תוכניות האימון, ניתוחי האימונים, היסטוריית הצ'אט, נתוני Garmin ומידע שהתקבל בעבר מ-Strava, וכן את חשבון ההתחברות שלך (Firebase). לאחר המחיקה לא ניתן לשחזר את החשבון. אם שלב כלשהו במחיקה לא הושלם, תוצג לך הודעה ותוכל/י לנסות שוב.</p>
          <p className="text-gray-300 mt-2">אם חיברת בעבר את Strava, אנו מבקשים גם לבטל את ההרשאה של RitmoIQ בחשבון ה-Strava שלך; אם הביטול לא מצליח, נציג לך הנחיה להסיר את RitmoIQ ידנית בהגדרות Strava. אם חיברת את Garmin, אנו מבקשים מ-Stridee למחוק את החשבון שלך אצלה.</p>
          <p className="text-gray-300 mt-2">עותקים של מידע שנמחק עשויים להישמר בגיבויים של ספקי התשתית שלנו לפרק זמן נוסף, בהתאם למחזורי הגיבוי שלהם.</p>
          <p className="text-gray-300 mt-2">מידע שהתקבל בעבר מ-Strava ומידע שהופק ממנו עשויים להישמר עד למחיקת החשבון או עד לבקשת מחיקה נפרדת. הסרת ההרשאה של RitmoIQ בהגדרות Strava אינה מוחקת בעצמה מידע שכבר נשמר אצלנו.</p>
        </section>

        {/* Strava */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-blue-400">8. Strava (החיבור הופסק)</h2>
          <p className="text-gray-300 mb-2">
            בעבר אפשרה האפליקציה לחבר חשבון Strava באמצעות Strava API, בהתאם ל<a href="https://www.strava.com/legal/api" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">תנאי השימוש של Strava</a>. ב-21 באוקטובר 2026 החיבור הופסק: לא ניתן עוד לחבר את Strava ל-RitmoIQ או לנהל את החיבור מתוך האפליקציה, ואיננו מקבלים מידע חדש מ-Strava.
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-1">
            <li>מידע שהתקבל בעבר מ-Strava עשוי להישמר עד למחיקת החשבון או עד לבקשת מחיקה נפרדת (ראו סעיף 7). בהתאם למימוש הנוכחי, הוא אינו נשלח לספקי ה-AI.</li>
            <li>אם חיברת בעבר את Strava, ייתכן שההרשאה של RitmoIQ עדיין מופיעה בחשבון ה-Strava שלך. ניתן להסיר אותה בכל עת בהגדרות Strava, בעמוד My Apps.</li>
            <li>איננו משנים או מוחקים נתונים בחשבון ה-Strava שלך.</li>
          </ul>
        </section>

        {/* Garmin Connect */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-blue-400">9. Garmin Connect (פיילוט סגור)</h2>
          <p className="text-gray-300 mb-2">החיבור ל-Garmin Connect באמצעות Stridee זמין כרגע למשתמשים שאושרו לפיילוט הסגור של RitmoIQ, והוא לבחירתך בלבד.</p>
          <ul className="list-disc list-inside text-gray-300 space-y-1">
            <li><strong>איך זה עובד:</strong> החיבור מתבצע באמצעות Stridee, ספק צד שלישי שמחבר את RitmoIQ ל-Garmin Connect. את החיבור מאשרים בדף ההסכמה של Stridee ו-Garmin. Stridee מקבלת מאיתנו מזהה אקראי, ולא את כתובת האימייל או את פרטי החשבון שלך ב-RitmoIQ. רק לאחר שאישרת את החיבור, Stridee מעבירה לנו את הפעילויות שהושלמו.</li>
            <li><strong>מה אנחנו מקבלים:</strong> לכל פעילות, את הקובץ המקורי של השעון (קובץ FIT). הקובץ עשוי לכלול נתוני מיקום (GPS).</li>
            <li><strong>מה אנחנו שומרים:</strong> הקובץ המקורי נשמר מוצפן ומוגדר להימחק אוטומטית לאחר 14 יום. מהקובץ אנו מפיקים סיכום אימון, כגון מועד, מרחק, זמן, קצב, דופק, הקפות, ספליטים לק"מ ומדדי ריצה שהשעון רשם (כגון קדנס). הסיכום נשמר ללא נתוני מיקום וללא המספר הסידורי של המכשיר. מאמן ה-AI מנתח פעילויות ריצה בלבד.</li>
            <li><strong>למה משמשים הנתונים:</strong> נתוני Garmin משמשים כדי להציג את הפעילויות שלך, לנתח אותן ולהשוות אותן לאימונים המתוכננים בתוכנית שלך, וכדי לשלוח לך התראה על אימון שהסתיים אם הפעלת התראות. ניתוח באמצעות AI נעשה רק בהסכמה נפרדת (ראו להלן).</li>
            <li><strong>ניתוח באמצעות AI (בהסכמה נפרדת):</strong> אם אישרת את סעיף ה-AI בהסכמת Garmin, מאמן ה-AI משתמש בסיכומי האימון האלה לניתוח אישי, להמלצות ולהתאמת תוכנית האימונים שלך. לשם כך נתוני אימון (כגון תאריך, מרחק, זמן, קצב, דופק והקפות) נשלחים לספקי ה-AI, OpenAI ו-Google Gemini, כמתואר בסעיף 4.1. נתוני מיקום, הקובץ המקורי, המספר הסידורי של המכשיר ומזהים פנימיים אינם נשלחים. בלי הסכמה זו, נתוני Garmin אינם נשלחים לשירות AI. RitmoIQ אינה משתמשת בנתונים אלה כדי לאמן מודלי AI משלה.</li>
            <li><strong>ביטול הסכמת ה-AI:</strong> ניתן לבטל את סעיף ה-AI בכל עת מתוך הפרופיל (חיבורי אימונים ← ניהול חיבור Garmin ← ביטול ההסכמה לניתוח AI), בפנייה אלינו בכתובת <LegalEmail /> או בניתוק Garmin. לאחר הביטול נתוני Garmin אינם נשלחים לניתוח AI, והחיבור והפעילויות נשמרים. הביטול חל מאותו רגע ואילך, ואינו חל על עיבוד שכבר בוצע.</li>
            <li><strong>ניתוק:</strong> ניתן לנתק את Garmin בכל עת מתוך הפרופיל. הניתוק מפסיק את הסנכרון, מוחק מיד את הקבצים המקוריים ששמורים אצלנו, ואנו מבקשים מ-Stridee לנתק את החיבור. סיכומי אימון שכבר הופקו נשמרים עד למחיקת החשבון או עד לבקשת מחיקה נפרדת.</li>
            <li><strong>מחיקת חשבון:</strong> מחיקת החשבון מוחקת גם את נתוני Garmin ששמורים אצלנו, ואנו מבקשים מ-Stridee למחוק את החשבון שלך אצלה (ראו סעיף 7).</li>
            <li><strong>ללא מכירה וללא פרסום:</strong> איננו מוכרים את נתוני Garmin ואיננו משתמשים בהם לפרסום.</li>
            <li><strong>מי עוד מעבד את הנתונים:</strong> Stridee (החיבור ל-Garmin Connect), Render (השרתים ומסד הנתונים של המערכת), OpenAI: OpenAI API לניתוח AI, רק בהסכמתך הנפרדת, ו-Google: Gemini API לניתוח AI, רק בהסכמתך הנפרדת, ו-Firebase Cloud Messaging, שמעביר התראות הכוללות את המרחק, משך הזמן והקצב של אימון שהסתיים. ספקים אלה מעבדים את נתוני Garmin כדי לספק לנו את השירותים שלהם, בהתאם להוראותינו, ואינם משתמשים בהם למטרותיהם, למעט שימושים מוגבלים שתנאיהם מתירים: לפי תנאי ה-API בתשלום של OpenAI ושל Gemini, OpenAI ו-Google אינן משתמשות בנתונים לאימון המודלים שלהן או לשיפור מוצריהן, אך שומרות אותם לזמן מוגבל אך ורק לצורך איתור ומניעה של שימוש לרעה.</li>
            <li><strong>נתונים ב-Garmin Connect:</strong> איננו משנים או מוחקים נתונים בחשבון ה-Garmin Connect שלך. שליחת אימונים ל-Garmin אינה חלק מהפיילוט.</li>
          </ul>
        </section>

        {/* עוגיות */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-blue-400">10. עוגיות (Cookies)</h2>
          <p className="text-gray-300">
            האפליקציה משתמשת בעוגיות חיוניות בלבד לצורך שמירת מצב התחברות ופעולתיות בסיסית.
            איננו משתמשים בעוגיות למעקב או פרסום.
          </p>
        </section>

        {/* שינויים במדיניות */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-blue-400">11. שינויים במדיניות הפרטיות</h2>
          <p className="text-gray-300">
            אנו עשויים לעדכן מדיניות זו מעת לעת. שינויים משמעותיים יפורסמו באפליקציה,
            ונעדכן את תאריך העדכון האחרון בראש המסמך.
          </p>
        </section>

        {/* יצירת קשר */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4 text-blue-400">12. יצירת קשר</h2>
          <p className="text-gray-300 mb-2">לשאלות בנוגע למדיניות הפרטיות:</p>
          <div className="bg-gray-800 rounded-lg p-4">
            <p className="text-gray-300"><strong>RitmoIQ</strong> — פרויקט המופעל באופן פרטי על ידי ליאור פרובקביץ</p>
            <p className="text-gray-300">אימייל: <LegalEmail /></p>
          </div>
        </section>

        {/* כפתור חזרה */}
        <div className="text-center mt-12">
          <a
            href={fromSite ? WEBSITE_URL : '/'}
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition"
          >
            {fromSite ? 'חזרה לאתר RitmoIQ' : 'חזרה לאפליקציה'}
          </a>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
